import { EarningsInsight } from '@toggle/toggle';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TooltipContainer } from '~/components/tooltip-container/TooltipContainer';
import { getEarningsInsightWithEventByEarningsId } from '~/react-query-hooks/events/use-event-feed/earnings-from-events';
import {
  useEventFeed,
  useMarkEventRead,
} from '~/react-query-hooks/events/use-event-feed/useEventFeed';
import { PosthogTrackingSource } from '~/services/tracking/posthog/constants';
import {
  SubTabsType,
  useEarningsInsightDrawer,
} from '~/widgets/earnings-insight-drawer/useEarningsInsightDrawer';

import { EarningsInsightIcon } from '../earnings-insight-icon/EarningsInsightIcon';
import { getEarningsInsightIconName } from '../earnings-insight-icon/utils';
import { EarningsInsightTooltip } from '../earnings-insight-tooltip/EarningsInsightTooltip';
import * as S from './EarningsInsightPill.styles';

export interface EarningsInsightPillProps {
  isPreview: boolean;
  entityTag: string;
  earningsInsight: EarningsInsight;
  isClickable?: boolean;
  source: PosthogTrackingSource;
}

export const EarningsInsightPill = ({
  isPreview,
  entityTag,
  earningsInsight,
  isClickable = true,
  source,
}: EarningsInsightPillProps) => {
  const { t } = useTranslation(['earnings']);
  const { openDrawer } = useEarningsInsightDrawer();
  const { mutate: markEventRead } = useMarkEventRead();

  const { data: eventFeedData } = useEventFeed(
    {
      entities: [entityTag],
      type: 'earnings-insight',
    },
    { enabled: !!entityTag }
  );
  const earningsInsightWithEvent = useMemo(() => {
    const events = eventFeedData?.pages?.flatMap(page => page.events) ?? [];
    return getEarningsInsightWithEventByEarningsId(
      events,
      earningsInsight.earnings_id
    );
  }, [eventFeedData?.pages, earningsInsight.earnings_id]);

  const eventId = earningsInsightWithEvent?.eventId ?? '';
  const read = earningsInsightWithEvent?.read ?? false;

  return (
    <TooltipContainer
      label={
        <EarningsInsightTooltip
          earningsInsight={earningsInsight}
          isPreview={isPreview}
          iconProps={{
            iconName: getEarningsInsightIconName(isPreview, true),
            viewBox: '0 0 30 32',
          }}
          read={read}
        />
      }
      placement="top-start"
      offsetY={-5}
      offsetX={8}
      hasMaxWidth={false}
      disabled={!isClickable}
      inPortal
    >
      <S.EarningsInsightWrapper
        data-testid="earnings-insight-pill"
        $isPreview={isPreview}
        $isClickable={isClickable}
        $isRead={read}
        onClick={e => {
          if (isClickable) {
            e.stopPropagation();
            if (eventId) {
              markEventRead(eventId);
            }
            openDrawer({
              entityTag,
              earningsId: earningsInsight.earnings_id,
              subtab: isPreview ? SubTabsType.Preview : SubTabsType.Recap,
              source,
            });
          }
        }}
      >
        <EarningsInsightIcon
          entityTag={entityTag}
          size={12}
          earningsInsight={earningsInsight}
          eventId={eventId}
          read={read}
          enableHover={false}
          isBordered={false}
          source={source}
        />
        <S.EarningsInsightLabel $isPreview={isPreview} $isRead={read}>
          {isPreview
            ? t('earnings:earningsInsight.drawer.earningsPreview.title')
            : t('earnings:earningsInsight.drawer.earningsRecap.title')}
        </S.EarningsInsightLabel>
      </S.EarningsInsightWrapper>
    </TooltipContainer>
  );
};
